// Network service layer. Components depend on this contract, not on any
// concrete implementation. Backed by the Run 2 evidence/inference API.
// (The Run 1 fixture providers are retained in this folder as a deterministic
// visual/regression asset and are intentionally not wired here.)

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SESSION_KEY = "tt_session_id";

// A fresh device may have localStorage present but throwing (Safari private
// mode / "Block All Cookies" / a locked-down first-run profile all make
// getItem/setItem raise instead of silently no-op'ing). Absence or denial of
// prior client state is normal first-visit state, not an error — fall back to
// an in-memory id for that tab/session rather than letting identity creation
// itself take down the boot flow.
let memorySessionId = null;

export function getSessionId() {
  try {
    let s = localStorage.getItem(SESSION_KEY);
    if (!s) {
      s = "guest_" + Math.random().toString(36).slice(2, 14);
      localStorage.setItem(SESSION_KEY, s);
    }
    return s;
  } catch {
    if (!memorySessionId) memorySessionId = "guest_" + Math.random().toString(36).slice(2, 14);
    return memorySessionId;
  }
}

export function setSessionId(s) {
  if (!s) return;
  try {
    localStorage.setItem(SESSION_KEY, s);
  } catch {
    memorySessionId = s;
  }
}

// Carries "which Edition did the anonymous deep-link visitor ask for" across
// the Google OAuth round trip. The redirect URL back from auth is always the
// bare origin (by design — see SaveTasteSheet), so this is a same-tab
// breadcrumb, not a URL param; sessionStorage (not localStorage) so it can't
// leak into an unrelated later visit if the redirect is ever abandoned.
const PENDING_EDITION_KEY = "tt_pending_edition";

export function setPendingEdition(id) {
  try {
    if (id) sessionStorage.setItem(PENDING_EDITION_KEY, id);
  } catch { /* best-effort; worst case the user lands on Profile instead */ }
}

export function takePendingEdition() {
  try {
    const id = sessionStorage.getItem(PENDING_EDITION_KEY);
    if (id) sessionStorage.removeItem(PENDING_EDITION_KEY);
    return id;
  } catch {
    return null;
  }
}

// Perceived-speed: the server hands us the NEXT round's cards one round-trip
// before they are rendered, but the browser won't start fetching their posters
// until React mounts the new <img>. Warming them during the (already existing)
// advance animation means the next matchup is typically served from cache
// instead of starting a cold fetch mid-transition. Detached and best-effort:
// it renders nothing, blocks nothing, and cannot fail the round.
export function preloadRoundArt(round) {
  if (!round || typeof Image === "undefined") return;
  for (const side of [round.left, round.right]) {
    const url = side && ((side.poster && side.poster.url) || side.artwork);
    if (url) {
      const img = new Image();
      img.decoding = "async";
      img.src = url;
    }
  }
}

// GET is safe to retry by HTTP semantics — it has no side effect to
// duplicate. Retry transient failures (network error / 5xx) a couple of
// times before surfacing them; never retry 4xx, which are real client-side
// rejections.
//
// POST is deliberately NOT retried here. Audited every POST endpoint this
// client calls: only submitChoice is server-side deduped (guarded by an
// existing-comparison_id check), everything else mints a fresh id on every
// call with no client-supplied idempotency key — startRun creates a new
// run_id, createChallenge/acceptChallenge/faceoffChoice/createFaceoffShare
// create new share/run records, authSession can mint a new session_token,
// logEvent inserts a new analytics row. If the server commits but the
// client loses the response (timeout, dropped connection), a blind retry
// would silently duplicate that side effect. Building per-endpoint
// idempotency keys is out of scope for this pass, so retries are simply
// restricted to the class of request that's safe by construction: GET.
const RETRY_DELAYS_MS = [300, 700];

async function withRetry(attempt) {
  let lastErr;
  for (let i = 0; i <= RETRY_DELAYS_MS.length; i++) {
    try {
      return await attempt();
    } catch (err) {
      lastErr = err;
      if (err.noRetry || i === RETRY_DELAYS_MS.length) break;
      await new Promise((r) => setTimeout(r, RETRY_DELAYS_MS[i]));
    }
  }
  throw lastErr;
}

// The backend's CORS layer only echoes back the caller's exact origin (as
// required by the fetch spec whenever a request carries `credentials:
// "include"`) once it sees an existing Cookie header on the request — see
// starlette's CORSMiddleware. A brand-new browser has no cookie for this
// domain yet, so a credentialed cross-origin call gets the literal wildcard
// `Access-Control-Allow-Origin: *` back, which browsers refuse to honor
// together with credentials — the fetch fails before the app ever sees a
// response. Only the handful of endpoints that actually read the auth cookie
// (session-linking / cross-device history) need `credentials: "include"`;
// every anonymous call — starting a run above all — must NOT set it, or a
// cookie-less fresh device can never get a response back at all.
async function post(path, body, { withAuthCookie = false } = {}) {
  // No retry — see the note above RETRY_DELAYS_MS. A single attempt only.
  const res = await fetch(API + path, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: withAuthCookie ? "include" : "omit",
    body: JSON.stringify(body || {}),
  });
  if (!res.ok) throw new Error(`${res.status}: ${await res.text()}`);
  return res.json();
}

async function get(path, { withAuthCookie = false } = {}) {
  return withRetry(async () => {
    const res = await fetch(API + path, { credentials: withAuthCookie ? "include" : "omit" });
    if (!res.ok) {
      const err = new Error(`${res.status}: ${await res.text()}`);
      err.noRetry = res.status < 500;
      throw err;
    }
    return res.json();
  });
}

export const api = {
  startRun: ({ layout, testType, editionId } = {}) =>
    post(
      "/runs",
      { session_id: getSessionId(), layout, test_type: testType, edition_id: editionId, user_agent: navigator.userAgent },
      // TASTE EDITIONS are authenticated-only server-side — send the cookie
      // only for an Edition start; the core anonymous flow never needs it.
      { withAuthCookie: !!editionId }
    ),
  submitChoice: (runId, body) => post(`/runs/${runId}/choice`, body),
  completeRun: (runId) => post(`/runs/${runId}/complete`, {}),
  getProfile: (runId) => get(`/runs/${runId}/profile`),
  simulate: (strategy) => post("/dev/simulate", { strategy }),
  simulateMatch: (a, b) => post(`/dev/simulate-match?a=${encodeURIComponent(a)}&b=${encodeURIComponent(b)}`, {}),
  logEvent: (type, runId, payload) =>
    post("/analytics", { session_id: getSessionId(), run_id: runId, type, payload: payload || {} }).catch(
      () => {}
    ),
  // viral
  createChallenge: (runId) => post(`/runs/${runId}/challenge`, {}),
  getChallenge: (id) => get(`/challenges/${id}`),
  acceptChallenge: (id, layout) => post(`/challenges/${id}/accept`, { session_id: getSessionId(), layout }),
  getFaceoff: (id) => get(`/faceoffs/${id}`),
  faceoffChoice: (id, body) => post(`/faceoffs/${id}/choice`, { session_id: getSessionId(), ...body }),
  createFaceoffShare: (runId) => post(`/runs/${runId}/faceoff-share`, {}),
  getMatch: (runId, otherId) => get(`/runs/${runId}/match/${otherId}`),
  // ---- GUESS MY TASTE (social guessing — guest-first, no account wall) ----
  startGuess: (runId, targetRunId, roundCount) =>
    post(`/runs/${runId}/guess`, { target_run_id: targetRunId, round_count: roundCount || undefined }),
  getGuessSession: (id) => get(`/guess-sessions/${id}`),
  startGuessSession: (id) => post(`/guess-sessions/${id}/start`, { session_id: getSessionId() }),
  submitGuess: (id, sequence, guessedSubjectId) =>
    post(`/guess-sessions/${id}/rounds/${sequence}/guess`, { guessed_subject_id: guessedSubjectId }),
  createGuessReciprocal: (id) => post(`/guess-sessions/${id}/reciprocal`, {}),
  // ---- TASTE EDITIONS ----
  // listEditions carries per-user "played" state -> needs the cookie when
  // signed in (harmless/ignored server-side when anonymous). getEdition is a
  // public deep-link detail lookup (e.g. the anonymous auth-gate screen) and
  // deliberately does NOT send it.
  listEditions: () => get("/editions", { withAuthCookie: true }),
  getEdition: (id) => get(`/editions/${id}`),
  // withAuthCookie: true — these resolve evidence across every session
  // linked to the signed-in user, which requires the auth cookie.
  getKeepers: (sessionId) => get(`/sessions/${sessionId || getSessionId()}/keepers`, { withAuthCookie: true }),
  getSessionProfile: (sessionId) => get(`/sessions/${sessionId || getSessionId()}/profile`, { withAuthCookie: true }),
  // Every Profile scope (FILM/SERIES/GAMES/SCREEN/CROSS-MEDIA) the session
  // currently has sufficient accumulated evidence for — the living, whole
  // Taste Profile, not just the last Test played. { scopes, defaultScope }.
  getProfileScopes: (sessionId) =>
    get(`/sessions/${sessionId || getSessionId()}/profiles`, { withAuthCookie: true }),
  // ---- identity (Emergent Managed Google Auth) ----
  authSession: (sessionId) =>
    post("/auth/session", { session_id: sessionId, anon_session_id: getSessionId() }, { withAuthCookie: true }),
  authMe: () => get("/auth/me", { withAuthCookie: true }),
  logout: () => post("/auth/logout", {}, { withAuthCookie: true }),
};
